import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../../../../utils/axios';

const initialState = {
    checkInStaffList: {
        data: []
    },
    loading: false
};

const teamSlice = createSlice({
    name: 'team',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(addCheckInStaffs.pending, (state) => {
                // will be added
            })
            .addCase(addCheckInStaffs.fulfilled, (state, action) => {
                const newStaff = Array.isArray(action.payload?.data)
                    ? action.payload?.data
                    : [action.payload?.data];

                state.checkInStaffList.data = [
                    ...state.checkInStaffList.data,
                    ...newStaff
                ];

                const sortedData = [...state.checkInStaffList.data].sort((a, b) => {
                    // Convert access to boolean and compare (true values come first)
                    return (b.access === true) - (a.access === true);
                });

                state.checkInStaffList.data = sortedData;

                // state.loading = false;
                // console.log("Response from addCheckInStaffs:", action.payload);
            })
            .addCase(addCheckInStaffs.rejected, (state, action) => {
                // state error handling will be added
                console.error("Error adding staff:", action.payload);
            });

        builder
            .addCase(getAllCheckInStaff.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAllCheckInStaff.fulfilled, (state, action) => {
                const sortedData = [...action.payload?.data].sort((a, b) => {
                    // Convert access to boolean and compare (true values come first)
                    return (b.access === true) - (a.access === true);
                });

                state.checkInStaffList.data = sortedData;
                state.loading = false;
                // console.log("Fetched staff list:", action.payload);
                // state.checkInStaffList.data = action.payload?.data;
                // state.loading = false;
                // console.log("Fetched staff list:", action.payload);
            })
            .addCase(getAllCheckInStaff.rejected, (state, action) => {
                state.loading = false;
                // console.error("Error fetching staff:", action.payload);
            });

        builder
            .addCase(updateCheckInStaffAccessStatusById.fulfilled, (state, action) => {
                const index = state.checkInStaffList.data.findIndex((row) => row.id === action.payload?.data?.id);
                if (index !== -1) {
                    state.checkInStaffList.data[index] = action.payload?.data;
                }
            })

        builder
            .addCase(regenerateInviteCode.fulfilled, (state, action) => {
                const index = state.checkInStaffList.data.findIndex((row) => row.id === action.payload?.data?.id);
                if (index !== -1) {
                    state.checkInStaffList.data[index] = action.payload?.data;
                }
            })

        builder
            .addCase(attachTicketTypeIds.fulfilled, (state, action) => {
                const index = state.checkInStaffList.data.findIndex((row) => row.id === action.payload?.data?.id);
                if (index !== -1) {
                    state.checkInStaffList.data[index] = action.payload?.data;
                }
            })

        builder
            .addCase(removeTicketTypeIds.fulfilled, (state, action) => {
                const index = state.checkInStaffList.data.findIndex((row) => row.id === action.payload?.data?.id);
                if (index !== -1) {
                    state.checkInStaffList.data[index] = action.payload?.data;
                }
            })
    }
});

export const { } = teamSlice.actions;

export default teamSlice.reducer;


/**
 * @async function: to fetch all check-in staff members for a given event by its ID
 * type can be 'staff' or 'attendee'
 * Handles errors and returns the API response
 * 
 * @example of usage: dispatch(getAllCheckInStaff({ eventId: 123, type: 'staff' }))
 */
export const getAllCheckInStaff = createAsyncThunk(
    'team/getAllCheckInStaff',
    async ({ eventId, type }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.get(`/api/v2/events/${eventId}/invitations/${type}`);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)

// ticket_type_ids: [1,2,3]
export const attachTicketTypeIds = createAsyncThunk(
    'team/attachTicketTypeIds',
    async ({ invitationId, payload }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/invitations/${invitationId}/ticket-types/attach`, payload);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }
            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)

export const removeTicketTypeIds = createAsyncThunk(
    'team/removeTicketTypeIds',
    async ({ invitationId, payload }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/invitations/${invitationId}/ticket-types/detach`, payload);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }
            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)


/**
 * @async function: to re-generate and resend the invitation email for a check-in staff member
 * by their id given by backend
 * Handles errors and returns the API response
 * 
 * @example of usage: dispatch(regenerateInviteCode({ recordId: 456 }))
 */
export const regenerateInviteCode = createAsyncThunk(
    'team/regenerateInviteCode',
    async ({ recordId }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/invitations/${recordId}/regenerate`);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)


/**
 * @async function: to attach one or more ticket types to a check-in staff member to give them access
 * Staff can only check-in attendees with the ticket types assigned to them
 * Handles errors and returns the API response
 * 
 * @example of usage: dispatch(attachTicketTypesToStaff({ invitationId: 456, payload: { ticket_type_ids: [1,2,3] } }))
 */
export const attachTicketTypesToStaff = createAsyncThunk(
    'team/attachTicketTypesToStaff',
    async ({ invitationId, payload }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/invitations/${invitationId}/tickets`, payload);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)


/**
 * @async function: to add one or more check-in staff members
 * It also works to send multiple invitations at once
 * Handles errors and returns the API response
 * 
 * @example of usage: dispatch(addCheckInStaffs({ eventId: 123, payload: { type: 'staff', invites: [{email: '', phone: '', name: ''},...] } }))
 */
export const addCheckInStaffs = createAsyncThunk(
    'team/addCheckInStaff',
    async ({ eventId, payload }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/events/${eventId}/invitations`, payload);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)


/**
 * @async function: to update the access status of a check-in staff member by their invitation ID
 * This is used to revoke or grant access
 * Handles errors and returns the API response
 * 
 * @example of usage: dispatch(updateCheckInStaffAccessStatusById({ invitationId: 456, payload: { access: false } }))
 */
export const updateCheckInStaffAccessStatusById = createAsyncThunk(
    'team/updateCheckInStaffAccessStatusById',
    async ({ invitationId, payload }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/invitations/${invitationId}/access`, payload);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)


export const getEventAttendanceDetails = createAsyncThunk(
    'team/getEventAttendanceDetails',
    async (sessionId, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/attendances/scan-page`, {}, {
                headers: {
                    "X-Session-ID": sessionId
                }
            });

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)

export const getCheckInDetailsByTicketId = createAsyncThunk(
    'team/getCheckInDetailsByTicketId',
    async ({ ticketId, sessionId }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/attendances/scan-status`, { ticketId: ticketId }, {
                headers: {
                    "X-Session-ID": sessionId
                }
            });

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)

// TIX-11455422 demo ticket id to test api

export const markedAttendanceByTicketId = createAsyncThunk(
    'team/markedAttendanceByTicketId',
    async ({ ticketId, sessionId, action, checkInById }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/attendances/scan-action`, {
                ticketId: ticketId,
                action: action,
                checked_in_by: checkInById
            }, {
                headers: {
                    "X-Session-ID": sessionId
                }
            });

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)


/**
 * @async
 * @function getAttendeesHistory
 * @description Fetches the attendance history of attendees checked in by a specific staff member for a given event.
 * 
 * @param {Object} params - The parameters for the function.
 * @param {string} params.userId - The ID of the staff member whose attendance history is to be fetched.
 * @param {string} params.eventId - The ID of the event for which the attendance history is to be fetched.
 * @param {Function} rejectWithValue - Function to handle rejected values in case of an error.
 * 
 * @returns {Object} - The response from the backend API, including attendance history and status code.
 * 
 * @example
 * dispatch(getAttendeesHistory({ userId: 'user-123', eventId: 'event-456' }))
 */
export const getAttendeesHistory = createAsyncThunk(
    'team/getAttendeesHistory',
    async ({ userId, eventId }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/attendance-history`, {
                type: 'staff',
                user_id: userId,
                event_id: eventId
            });

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)


/**
 * @async
 * @function getAttendeeTicketDetails
 * @description Fetches the ticket details of an attendee using their ticket ID. Including scanning history and personal information.
 * 
 * @param {Object} params - The parameters for the function.
 * @param {string} params.ticketId - The ID of the ticket to fetch details for.
 * @param {Function} rejectWithValue - Function to handle rejected values in case of an error.
 * 
 * @returns {Object} - The response from the backend API, including ticket details and status code.
 * 
 * @example
 * dispatch(getAttendeeTicketDetails({ ticketId: 'TIX-12345678' }))
 */
export const getAttendeeTicketDetails = createAsyncThunk(
    'team/getAttendeeTicketDetails',
    async ({ ticketId }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/attendance-history`, {
                type: 'attendee',
                ticket_id: ticketId
            });

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)

/**
 * @async
 * @function manualSearchTicket
 * @description Manually searches for tickets using various criteria such as ticket ID, email, phone number, or attendee name.
 * This function is useful for finding tickets when scanning is not possible.
 * 
 * @param {Object} params - The parameters for the function.
 * @param {string} params.sessionId - The session ID for authentication.
 * @param {Object} params.payload - The search criteria payload which may include:
 */
export const manualSearchTicket = createAsyncThunk(
    'team/manualSearchTicket',
    async ({ sessionId, payload }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(
                `/api/v2/attendance/search-tickets-with-checks`,
                payload,
                {
                    headers: {
                        "X-Session-ID": sessionId
                    }
                });

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)

// "id"	
// "event_id"	
// "user_id"	
// "type"	
// "name"	
// "email"	
// "phone"	
// "invite_code"	
// "session_id"	
// "access"	
// "response"	
// "created_at"	
// "updated_at"	
// "responded_at"